import React, { useState, MouseEvent, useContext, useRef, useEffect } from 'react'
import { WHATSAPP_NUMBER } from '@constants/contact'
import Donators from '@components/Donator/Donators'
import CompatibilityBanner from '@components/CompatibilityBanner/CompatibilityBanner'
import Poligono from '../../resources/imgs/polygon-1.svg'
import BannerBlack from '../../resources/imgs/banner-blacknovember2.png' // <-- existing import
import '@resources/fontes/fontes.css'
import 'swiper/css/navigation'
import CatalogFooter from '@components/CatalogFooter/CatalogFooter'
import CatalogHeader from '@components/CatalogHeader/CatalogHeader'
import { Modal } from 'react-bootstrap'
import ModalFilters from '@components/ModalFilters/ModalFilters'
import { FaTimes, FaSyncAlt, FaInfoCircle } from 'react-icons/fa'
import { GoChevronLeft, GoChevronRight } from 'react-icons/go'
import { IoChevronDown, IoChevronUp } from 'react-icons/io5'
import { IoIosSearch } from 'react-icons/io'
import '@resources/css/catalogo/home.css'
import { LuSettings2 } from 'react-icons/lu'
import DonorsContext from '@contexts/Donors'
import { useSearchParams, useLocation } from 'react-router-dom' // <-- added useLocation
import LoadingSpinner from '@components/LoadingSpinner/LoadingSpinner'
import JourneySteps from './JourneySteps'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import AuthContext from '@contexts/Auth'

const Home: React.FC<any> = () => {
  const { onFavoriteDonor, cart } = useContext(AuthContext)

  const {
    donors,
    setDonors,
    compatibleDonors,
    bannerState,
    pagination,
    isLoading,
    options,
    filters,
    setFilters,
    handleApplyFilters,
    handleClearFilters,
    handleClearSelectedFilters,
    handleNextPage,
    selectedFilters
  } = useContext(DonorsContext)

  const activeFilterCount = React.useMemo(() => {
    let count = 0
    Object.entries(selectedFilters ?? {}).forEach(([, value]) => {
      if (Array.isArray(value)) count += value.length
    })
    if (selectedFilters?.heightMin > 0 || selectedFilters?.heightMax > 0) count += 1
    if (selectedFilters?.weightMin > 0 || selectedFilters?.weightMax > 0) count += 1
    if (selectedFilters?.sampleValueMin > 0 || selectedFilters?.sampleValueMax > 0) count += 1
    return count
  }, [selectedFilters])

  const [showModal, setShowModal] = useState<{ show: boolean, title: string, filterType: string }>({ show: false, title: '', filterType: '' })
  const [cartDonorIds, setCartDonorIds] = useState<string[]>([])
  const [addingToCart, setAddingToCart] = useState<string[]>([])

  const [searchParams] = useSearchParams()
  const location = useLocation() // <-- new: to detect navigation within app

  const treatmentLabel = React.useMemo(() => {
    const raw = [...searchParams.getAll('treatmentTypes'), ...searchParams.getAll('treatmentTypes[]')]
      .flatMap(value => value.includes(',') ? value.split(',') : [value])
      .map(value => value.toLowerCase())

    const labels = [] as string[]
    if (raw.includes('icsi')) labels.push('ICSI')
    if (raw.includes('fiv')) labels.push('FIV')

    return labels.join(' e ') || 'seu tratamento'
  }, [searchParams])

  const [openCharacter, setOpenCharacter] = useState(false)

  const [showCompatible, setShowCompatible] = useState<boolean>(false)

  useEffect(() => {
    setShowCompatible(false)
  }, [bannerState, treatmentLabel])

  // NEW: banner visibility state
  const [showBanner, setShowBanner] = useState<number>(0)

  const [isSearching, setIsSearching] = useState(false)

  const searchButtonRef = useRef<HTMLButtonElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  const SweetAlert = withReactContent(Swal)

  // Capture saved scroll position on mount BEFORE any listener can overwrite it
  const savedScrollY = useRef(sessionStorage.getItem('homeScrollY'))
  const scrollRestored = useRef(false)

  // Save scroll position to sessionStorage on scroll (throttled), only after restore is done
  useEffect(() => {
    let ticking = false
    const onScroll = () => {
      if (!scrollRestored.current) return
      if (!ticking) {
        ticking = true
        requestAnimationFrame(() => {
          sessionStorage.setItem('homeScrollY', String(window.scrollY))
          ticking = false
        })
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Restore scroll position after donors load on reload
  useEffect(() => {
    if (!isLoading && donors.length > 0 && !scrollRestored.current) {
      scrollRestored.current = true
      const y = parseInt(savedScrollY.current || '0', 10)
      if (y > 0) {
        // Wait for the DOM to render all donor cards before scrolling
        setTimeout(() => {
          window.scrollTo(0, y)
        }, 100)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, donors])

  useEffect(() => {
    if (cart?.donor_ids) {
      const ids = cart.donor_ids.split(' / ').filter(Boolean)
      setCartDonorIds(ids)
    }
  }, [cart])

  useEffect(() => {
    if (searchParams.has('verified') && searchParams.get('verified') === '1') {
      SweetAlert.fire({
        title: 'E-mail verificado com sucesso!',
        icon: 'success',
        confirmButtonText: 'OK'
      })
    }
  }, [searchParams, SweetAlert])

  // NEW: show banner when component mounts OR when location changes (every navigation to Home),
  // only if now is between 02/11/2025 00:00 and 14/11/2025 23:59 (local time).
  useEffect(() => {
    if (showBanner === 2) return // already shown during this session
    const now = new Date()
    const start = new Date(2025, 10, 2, 0, 0, 0, 0) // Nov = month 10 (0-based) -> 02/11/2025 00:00
    const end = new Date(2025, 10, 21, 23, 59, 59, 999)
    if (now >= start && now <= end) setShowBanner(1)
  }, [location, showBanner])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      searchButtonRef.current?.click()
    }
  }

  const handleCharacter = () => {
    setOpenCharacter(!openCharacter)
  }

  // NEW: close banner handler (user can dismiss during the session)
  const closeBanner = (e?: React.MouseEvent) => {
    // prevent the click from propagating to the anchor (so the close button never opens the link)
    e?.stopPropagation()
    setShowBanner(2)
  }

  const handleShowModal = (title: string, filterType: string) => {
    setShowModal({ show: true, title, filterType })
  }

  const handleCloseModal = () => {
    showModal.filterType === 'spermBank' && setFilters({ ...filters, spermBank: [] })
    setShowModal({ show: false, title: '', filterType: '' })
  }

  const clearFilters = (filterType: string) => {
    handleClearSelectedFilters(filterType)
    handleCloseModal()
  }

  const handleSearch = async () => {
    setIsSearching(true)
    try {
      await handleApplyFilters()
    } finally {
      setIsSearching(false)
    }
  }

  const handleFilter = () => {
    handleApplyFilters()
    setShowModal({ show: false, title: '', filterType: '' })
  }

  const handleRightClick = (event: MouseEvent<HTMLDivElement>) => {
    const parentElement = event.currentTarget.closest('.row')?.querySelector('.box-filter')
    if (parentElement) {
      parentElement.scrollLeft += 100 // Ajuste a distância de rolagem conforme necessário
    }
  }

  const handleLeftClick = (event: MouseEvent<HTMLDivElement>) => {
    const parentElement = event.currentTarget.closest('.row')?.querySelector('.box-filter')
    if (parentElement) {
      parentElement.scrollLeft -= 100 // Ajuste a distância de rolagem conforme necessário
    }
  }

  const getFilterText = (key: string) => {
    return options[key].filter((option: any) => filters[key].includes(String(option.key ?? option)))?.map((option: any) => option.value ?? option).join(', ') || ''
  }

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        handleNextPage()
      }
    }, { threshold: 1.0 })

    if (bottomRef.current) {
      observer.observe(bottomRef.current)
    }

    return () => {
      if (bottomRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.unobserve(bottomRef.current)
      }
    }
  }, [bottomRef, handleNextPage])

  const handleFavorite = (id: string) => {
    onFavoriteDonor(id, (isFavorited: boolean) => {
      setDonors((prevDonors) => prevDonors.map((donor) => {
        if (donor.internal_id === id) {
          return { ...donor, is_favorited: isFavorited }
        }

        return donor
      }))
    })
  }

  return (
    <>
      <CatalogHeader />

      {/* Promo banner overlay (shows centered) */}
      {showBanner === 1 && (
        <div className="promo-banner-overlay" aria-modal="true">
          <div className="promo-banner">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=Quero+saber+mais+sobre+as+condi%C3%A7%C3%B5es+Black+November+para+amostras+de+doadores%21`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()} // avoid any outer click behavior (defensive)
            >
              <img src={BannerBlack} alt="Black November - Pro-Seed" className="promo-banner-image" />
            </a>
            <button className="promo-close" aria-label="Fechar banner" onClick={closeBanner}>×</button>
          </div>
        </div>
      )}

      <LoadingSpinner isLoading={isLoading} text="Buscando doadores..." />

      <div className="home-page">

        <section className="top-box">
          <div className="container">
            <div className="row position-relative">
              <div className="text">
                <h4 className="m-0">Encontre o seu doador de sêmen</h4>
                <p className="m-0">O maior catálogo de doadores de sêmen nacionais e importados da América Latina</p>
              </div>
              <div className="polygon-wrapper">
                <img className="polygon" alt="Polygon" src={Poligono} />
              </div>
            </div>
          </div>
        </section>

        <JourneySteps />

        <div className="container mt-5">
          <div className="home-filters-sticky">
            <div className="row justify-content-between align-items-center no-gutters">
              <div className="p-0 col-md-12 position-relative">
                {/* <div className="prevtBtn" onClick={handleLeftClick}><GoChevronLeft /></div> */}
                {/* <div className="nextBtn" onClick={handleRightClick}><GoChevronRight /></div> */}
                <div className="box-filter pt-2 pb-2">
                  <button className="font-weight-bold btn-all btn btn-primary" onClick={() => handleShowModal('Todos os Filtros', 'all')}><LuSettings2 size={20} className="mr-2"/>Todos os Filtros</button>
                  <button className="btn font-weight-bold" onClick={() => handleShowModal('Tratamentos', 'treatmentTypes')}>Tratamentos
                    {searchParams.get('treatmentTypes') && (<span className="number">{searchParams.get('treatmentTypes')?.split(',').length}</span>)}
                  </button>
                  <button className={`btn font-weight-bold ${openCharacter ? 'btn-primary' : ''}`} onClick={handleCharacter}>Características {openCharacter ? <IoChevronUp /> : <IoChevronDown />}</button>
                  <button className="btn font-weight-bold" onClick={() => handleShowModal('Bancos de Origem', 'spermBanks')}>Bancos de Origem
                    {searchParams.get('spermBanks') && (<span className="number">{searchParams.get('spermBanks')?.split(',').length}</span>)}
                  </button>
                  <button className="btn font-weight-bold" onClick={() => handleShowModal('Tipo de Doador', 'donorTypes')}>Tipo de Doador
                    {searchParams.get('donorTypes') && (<span className="number">{searchParams.get('donorTypes')?.split(',').length}</span>)}
                  </button>
                  <button className="btn font-weight-bold" onClick={() => handleShowModal('Doador com Fotos', 'donorsWithPhotos')}>Doador com Fotos
                    {searchParams.get('donorsWithPhotos') && (<span className="number">1</span>)}
                  </button>
                  <div className="form-group mb-0 position-relative w-100">
                    <input
                      type="text"
                      onKeyDown={handleKeyDown}
                      className="min-width-input form-control w-100"
                      placeholder="ID ou Apelido"
                      value={filters?.internalId}
                      onChange={(e) => setFilters({ ...filters, internalId: e.target.value })}
                    />
                    <button className="search-button mr-4" ref={searchButtonRef} onClick={handleSearch} disabled={isSearching || isLoading}>
                      {isSearching ? <div className="spinner-border spinner-border-sm" role="status"></div> : <IoIosSearch />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className={`mt-3 row position-relative ${openCharacter ? '' : 'd-none'}`}>
              <div className="prevtBtn" onClick={handleLeftClick}><GoChevronLeft /></div>
              <div className="nextBtn" onClick={handleRightClick}><GoChevronRight /></div>
              <div className="bt-1 box-filter col-md-12 pt-4 pb-2">
                <button className="font-weight-bold btn" onClick={() => handleShowModal('Cor da Pele', 'skinColors')}>Cor da Pele
                  {searchParams.get('skinColors') && (<span className="number">{searchParams.get('skinColors')?.split(',').length}</span>)}
                </button>
                <button className="font-weight-bold btn" onClick={() => handleShowModal('Cor dos Olhos', 'eyeColors')}>Cor dos Olhos
                  {searchParams.get('eyeColors') && (<span className="number">{searchParams.get('eyeColors')?.split(',').length}</span>)}
                </button>
                <button className="font-weight-bold btn" onClick={() => handleShowModal('Tipo de Cabelo', 'hairTypes')}>Tipo de Cabelo
                  {searchParams.get('hairTypes') && (<span className="number">{searchParams.get('hairTypes')?.split(',').length}</span>)}
                </button>
                <button className="font-weight-bold btn" onClick={() => handleShowModal('Cor do Cabelo', 'hairColors')}>Cor do Cabelo
                  {searchParams.get('hairColors') && (<span className="number">{searchParams.get('hairColors')?.split(',').length}</span>)}
                </button>
                <button className="font-weight-bold btn" onClick={() => handleShowModal('Raça', 'races')}>Raça
                  {searchParams.get('races') && (<span className="number">{searchParams.get('races')?.split(',').length}</span>)}
                </button>
                <button className="font-weight-bold btn" onClick={() => handleShowModal('Tipo Sanguíneo', 'bloodGroups')}>Tipo Sanguíneo
                  {searchParams.get('bloodGroups') && (<span className="number">{searchParams.get('bloodGroups')?.split(',').length}</span>)}
                </button>
                <button className="font-weight-bold btn" onClick={() => handleShowModal('Mapeamento Genético', 'geneticMappings')}>Mapeamento Genético
                  {searchParams.get('geneticMappings') && (<span className="number">1</span>)}
                </button>
                <button className="font-weight-bold btn" onClick={() => handleShowModal('Doadores Novos', 'newDonors')}>Doadores Novos
                  {searchParams.get('newDonors') && (<span className="number">1</span>)}
                </button>
                <button className="font-weight-bold btn" onClick={() => handleShowModal('Altura', 'height')}>Altura
                  {filters?.heightMin > 0 && (<span className="number">1</span>)}
                </button>
                <button className="font-weight-bold btn" onClick={() => handleShowModal('Peso', 'weight')}>Peso
                  {filters?.weightMin > 0 && (<span className="number">1</span>)}
                </button>
                <button className="font-weight-bold btn" onClick={() => handleShowModal('Valor de Amostra', 'sampleValue')}>Valor de Amostra
                  {filters?.sampleValueMin > 0 && (<span className="number">1</span>)}
                </button>
              </div>
            </div>
            <div className="row mt-3 mb-3">
              <div className="col-12 d-flex align-items-center">
                <h6 className="mb-0 text-primary-especial">{pagination.total} Doadores Encontrados</h6>
              </div>
            </div>
            <div className="row mt-3">
              <div className="box-filter current-filters col-md-12 mb-4">
                {Array.from(searchParams.keys()).map((key) => {
                  if (filters[key]?.length === 0 || key === 'hasFilter' || key === 'heightMax' || key === 'weightMax' || key === 'sampleValueMax') return null
                  let title
                  let text = ''

                  switch (key) {
                  case 'spermBanks':
                    title = 'Bancos de Origem'
                    text = getFilterText(key)
                    break
                  case 'treatmentTypes':
                    title = 'Tratamentos'
                    text = getFilterText(key)
                    break
                  case 'skinColors':
                    title = 'Cor da Pele'
                    text = getFilterText(key)
                    break
                  case 'faceShapes':
                    title = 'Tipo de Rosto'
                    text = getFilterText(key)
                    break
                  case 'eyeColors':
                    title = 'Cor dos Olhos'
                    text = getFilterText(key)
                    break
                  case 'distanceBetweenEyes':
                    title = 'Distância entre os olhos'
                    text = getFilterText(key)
                    break
                  case 'hairColors':
                    title = 'Cor do Cabelo'
                    text = getFilterText(key)
                    break
                  case 'hairTypes':
                    title = 'Tipo de Cabelo'
                    text = getFilterText(key)
                    break
                  case 'races':
                    title = 'Raça'
                    text = getFilterText(key)
                    break
                  case 'bloodGroups':
                    title = 'Tipo Sanguíneo'
                    text = getFilterText(key)
                    break
                  case 'eyebrows':
                    title = 'Espessura das Sobrancelhas'
                    text = getFilterText(key)
                    break
                  case 'donorTypes':
                    title = 'Tipo de Doador'
                    text = getFilterText(key)
                    break
                  case 'geneticMappings':
                    title = 'Mapeamento Genético'
                    text = getFilterText(key)
                    break
                  case 'newDonors':
                    title = 'Doadores Novos'
                    text = 'Sim'
                    break
                  case 'donorsWithPhotos':
                    title = 'Doador com Fotos'
                    text = 'Sim'
                    break
                  case 'heightMin':
                    title = 'Altura'
                    text = `${filters['heightMin']}cm - ${filters['heightMax']}cm`
                    break
                  case 'weightMin':
                    title = 'Peso'
                    text = `${filters['weightMin']}kg - ${filters['weightMax']}kg`
                    break
                  case 'sampleValueMin':
                    title = 'Valor de Amostra'
                    text = `${Number(filters['sampleValueMin']).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 })} - ${Number(filters['sampleValueMax']).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                    break
                  case 'noseShapes':
                    title = 'Tipos de Nariz'
                    text = filters[key].map((item) => item).join(', ')
                    break
                  case 'internalId':
                    title = 'Código do Doador'
                    text = filters[key]
                    break
                  }

                  if (filters[key] > 0 && (key == 'heightMin' || key == 'weightMin' || key == 'sampleValueMin')) {
                    return (
                      <button className="btn" key={key}>
                        <strong>{title}:</strong> {filters[key] ? text : ''} <FaTimes size={10} onClick={() => clearFilters(key)} />
                      </button>
                    )
                  }

                  if (key != 'heightMin' && key != 'weightMax' && key != 'sampleValueMin' && key != 'page' && key != 'login' && key != 'verified') {
                    return (
                      <button className="btn" key={key}>
                        {title}: {filters[key] ? text : ''} <FaTimes size={10} onClick={() => clearFilters(key)} />
                      </button>
                    )
                  }
                })}
                {filters.hasFilter && <button type="button" className="btn btn-warning" onClick={handleClearFilters}>Limpar filtros</button>}
              </div>
            </div>
          </div>
          {bannerState !== 'none' && (
            <div className="row mb-4">
              <div className="col-12">
                <p className="treatment-compat-notice mb-0">
                  <FaInfoCircle className="mr-2" />
                  {bannerState === 'offer'
                    ? `Doadores com amostra para Inseminação também atendem ${treatmentLabel}. O doador que não está na lista aparece ao final dos resultados.`
                    : `Doadores com amostra para Inseminação também atendem ${treatmentLabel}. Todos já estão nesta lista.`}
                </p>
              </div>
            </div>
          )}

          <div className="donators">
            {!isLoading && (donors?.length > 0 ? donors?.map((donor) => (
              <Donators key={donor.id} donor={donor} handleFavorite={handleFavorite} cartDonorIds={cartDonorIds} addingToCart={addingToCart} setAddingToCart={setAddingToCart} />
            )) : (
              <div className="row mt-5">
                <div className="col-12 text-center">
                  <h6>Nenhum doador encontrado</h6>
                </div>
              </div>
            ))}
            {!isLoading && (
              <CompatibilityBanner
                state={bannerState}
                count={compatibleDonors.length}
                treatmentLabel={treatmentLabel}
                isOpen={showCompatible}
                onToggle={() => setShowCompatible(prev => !prev)}
              />
            )}
            {!isLoading && bannerState === 'offer' && showCompatible && compatibleDonors.map((donor) => (
              <Donators key={`compat-${donor.id}`} donor={donor} handleFavorite={handleFavorite} cartDonorIds={cartDonorIds} addingToCart={addingToCart} setAddingToCart={setAddingToCart} compatibleBadge />
            ))}
          </div>
          <div ref={bottomRef} />
        </div>
        <Modal
          show={showModal.show}
          onHide={handleCloseModal}
          size="xl"
          dialogClassName="filter-modal"
        >
          <Modal.Header className="border-0 text-left">
            <div className="filter-modal-title">
              <span className="filter-modal-badge"><LuSettings2 /></span>
              <div>
                <div className="filter-modal-heading">{showModal.title}</div>
                <div className="filter-modal-subheading">REFINAR DOADORES COMPATÍVEIS</div>
              </div>
            </div>
            <button type="button" className="close" onClick={handleCloseModal}>
              <span aria-hidden="true">×</span>
            </button>
          </Modal.Header>

          <Modal.Body>
            <div className="filters-scroll">
              <ModalFilters filterType={showModal.filterType} />
            </div>
            <div id="apply-filter">
              <div className="container-fluid">
                <div className="row justify-content-center align-items-stretch">
                  <div className="col-5 p-2">
                    <button className="btn btn-outline-primary h-100 w-100 p-2" onClick={() => clearFilters(showModal.filterType)}><FaSyncAlt size={13} className="mr-2" />Limpar</button>
                  </div>
                  <div className="col-7 p-2">
                    <button className="btn btn-primary w-100 p-2" onClick={handleFilter}>Aplicar Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}</button>
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
      <CatalogFooter />
    </>
  )
}

export default Home
